/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * useGeolocation Hook
 *
 * Detects user's country based on IP address and provides
 * country-specific configuration for forms (phone formats, tax IDs, etc.)
 */

import { useState, useEffect } from 'react';
import { countries, Country, getCountryConfig, CountryConfig } from '@/data/countries';

interface GeolocationState {
  country: Country | null;
  countryConfig: CountryConfig | null;
  isLoading: boolean;
  error: string | null;
}

interface GeolocationResult extends GeolocationState {
  /** Get config for a specific country by name */
  getConfigForCountry: (countryName: string) => CountryConfig | null;
  /** Refresh geolocation detection */
  refresh: () => void;
}

const STORAGE_KEY = 'user_geolocation';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

interface CachedGeolocation {
  countryCode: string;
  timestamp: number;
}

/**
 * Hook to detect user's country via IP geolocation
 * Uses ip-api.com (free, no API key required)
 */
export function useGeolocation(): GeolocationResult {
  const [state, setState] = useState<GeolocationState>({
    country: null,
    countryConfig: null,
    isLoading: true,
    error: null,
  });

  const detectCountry = async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Check cache first
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        const { countryCode, timestamp }: CachedGeolocation = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          const country = countries.find(c => c.code === countryCode);
          if (country) {
            setState({
              country,
              countryConfig: getCountryConfig(country.code),
              isLoading: false,
              error: null,
            });
            return;
          }
        }
      }

      // Fetch from IP geolocation API
      const response = await fetch('http://ip-api.com/json/?fields=countryCode,country');

      if (!response.ok) {
        throw new Error('Failed to detect location');
      }

      const data = await response.json();

      if (data.countryCode) {
        // Cache the result
        const cacheData: CachedGeolocation = {
          countryCode: data.countryCode,
          timestamp: Date.now(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cacheData));

        // Find country in our data
        const country = countries.find(c => c.code === data.countryCode);

        if (country) {
          setState({
            country,
            countryConfig: getCountryConfig(country.code),
            isLoading: false,
            error: null,
          });
        } else {
          // Country not in our list, fallback to default
          const defaultCountry = countries.find(c => c.code === 'GN')!;
          setState({
            country: defaultCountry,
            countryConfig: getCountryConfig('GN'),
            isLoading: false,
            error: null,
          });
        }
      } else {
        throw new Error('Could not determine country');
      }
    } catch (error) {
      console.warn('Geolocation detection failed, using default:', error);
      // Fallback to Guinea as default
      const defaultCountry = countries.find(c => c.code === 'GN')!;
      setState({
        country: defaultCountry,
        countryConfig: getCountryConfig('GN'),
        isLoading: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  useEffect(() => {
    detectCountry();
  }, []);

  const getConfigForCountry = (countryName: string): CountryConfig | null => {
    const country = countries.find(c => c.name === countryName);
    if (country) {
      return getCountryConfig(country.code);
    }
    return null;
  };

  return {
    ...state,
    getConfigForCountry,
    refresh: detectCountry,
  };
}

export default useGeolocation;
